/**
 * LearnTimedQuiz.jsx — the gold-standard drill. User reads a passage
 * at their own pace with a running timer; taps "Done" when finished;
 * then answers the comprehension quiz. Adjusted WPM = raw_wpm ×
 * comprehension_pct / 100 becomes the leaderboard-ranking score.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpenCheck, Play, StopCircle, ArrowLeft, ChevronDown, Clock } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { readCustomPassage } from '../utils/learnCustomPassage';
import LearnQuiz from '../components/LearnQuiz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useTicker = (running) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [running]);
  return now;
};

const LearnTimedQuiz = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passageIdx, setPassageIdx] = useState(0);
  const [state, setState] = useState('setup'); // setup|reading|quiz|done
  const startedAt = useRef(0);
  const durationRef = useRef(0);

  useEffect(() => {
    const custom = readCustomPassage();
    if (custom) { setPassages([custom]); return; }
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/learn/passages`);
        if (r.ok) { const d = await r.json(); setPassages(d.passages || []); }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const passage = passages[passageIdx];
  const now = useTicker(state === 'reading');
  const elapsedMs = state === 'reading' ? Math.max(0, now - startedAt.current) : durationRef.current;

  const start = () => { startedAt.current = Date.now(); setState('reading'); };
  const stop = () => {
    durationRef.current = Date.now() - startedAt.current;
    setState('quiz');
  };
  const reset = () => { setState('setup'); durationRef.current = 0; };

  if (!passage) {
    return (
      <main className="sharp-bg min-h-screen flex items-center justify-center text-white/60" data-testid="learn-timed-loading">
        Loading passages…
      </main>
    );
  }

  const secs = Math.floor(elapsedMs / 1000);
  const mm = String(Math.floor(secs / 60)).padStart(1, '0');
  const ss = String(secs % 60).padStart(2, '0');

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="learn-timed-page">
      <button
        onClick={() => navigate('/learn')}
        className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to LEARN
      </button>

      <header className="mb-6 flex items-center gap-3">
        <BookOpenCheck className="w-6 h-6 text-amber-300" />
        <div>
          <p className="eyebrow text-amber-300 mb-0.5">Drill · Timed passage + quiz</p>
          <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">
            Read. Time. Prove.
            <span className="text-amber-300">.</span>
          </h1>
        </div>
      </header>

      {state === 'setup' && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Passage</p>
            <div className="relative">
              <select
                value={passageIdx}
                onChange={(e) => setPassageIdx(Number(e.target.value))}
                className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-amber-400/60"
                data-testid="learn-timed-passage-select"
              >
                {passages.map((p, i) => (
                  <option key={p.passage_id} value={i}>
                    {p.title} — {p.author} ({p.word_count} words)
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>
          <p className="text-xs text-white/50 leading-relaxed">
            When you tap <span className="text-amber-300 font-black">Start</span>, the passage appears and a timer runs.
            Read at your natural pace. Tap <span className="text-amber-300 font-black">Done</span> the instant you finish
            reading — WPM is calculated then. A 3-question comprehension quiz follows.
          </p>
          <button
            onClick={start}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-timed-start"
          >
            <Play className="w-4 h-4" /> Start
          </button>
        </div>
      )}

      {state === 'reading' && (
        <div className="space-y-4">
          <div
            className="flex items-center justify-between rounded-xl bg-black/40 border border-amber-500/30 px-4 py-2.5"
            data-testid="learn-timed-timer-row"
          >
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Time
            </p>
            <p
              className="font-display text-3xl font-black text-white tabular-nums"
              data-testid="learn-timed-timer"
            >
              {mm}:{ss}
            </p>
          </div>
          <div
            className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-amber-500/20 p-6 text-white/90 leading-relaxed text-base sm:text-lg"
            data-testid="learn-timed-passage-text"
          >
            {passage.text}
          </div>
          <button
            onClick={stop}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-timed-done"
          >
            <StopCircle className="w-4 h-4" /> Done — score me
          </button>
        </div>
      )}

      {state === 'quiz' && (
        <LearnQuiz
          passage={passage}
          drillKind="timed_quiz"
          durationMs={durationRef.current}
          onDone={() => setState('done')}
        />
      )}

      {state === 'done' && (
        <div className="mt-5 flex gap-3">
          <button
            onClick={reset}
            className="flex-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-3 text-sm font-black"
            data-testid="learn-timed-again"
          >
            Run another drill
          </button>
          <button
            onClick={() => navigate('/learn')}
            className="rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-3 text-sm font-black"
          >
            Back to LEARN
          </button>
        </div>
      )}
    </main>
  );
};

export default LearnTimedQuiz;
